import { useEffect } from 'react';
import type { CountryCode, Movie } from '@filmsloth/shared';
import { isCommunityMovie, providerLinks } from '@filmsloth/shared';
import { useGenreLabel, useMovieTitle, useRating, useT } from '../i18n/index.js';
import { TrailerPlayer } from '../components/TrailerPlayer.js';
import { useWatchlist } from '../session/useWatchlist.js';

// VITE_PAYPAL_URL overrides this without a code change.
const PAYPAL_URL = import.meta.env.VITE_PAYPAL_URL ?? 'https://www.paypal.me/jomahl';

// What people spend deciding what to watch, absent this app. Widely cited
// figure from a 2016 Reelgood/Learndipity survey of streaming viewers.
const AVERAGE_DECISION_MINUTES = 18;

// paypal.me accepts an amount as a URL segment; a VITE_PAYPAL_URL override
// that isn't a paypal.me link just gets an inert suffix, same as today.
function paypalAmountUrl(amount: number): string {
  return `${PAYPAL_URL}/${amount}`;
}

export function FinishedScreen({
  finalPick,
  country,
  sessionDurationMinutes,
  nextRoundCode,
  onShowResults,
  onNextRound,
  onNewSession,
}: {
  finalPick: Movie | null;
  country: CountryCode;
  /** Minutes from session start to the host's watch decision, if known. */
  sessionDurationMinutes: number | null;
  /** Set once somebody started another round from this one. */
  nextRoundCode: string | null;
  onShowResults: () => void;
  /** Starts another round with the same group, or follows into one. */
  onNextRound: () => void;
  onNewSession: () => void;
}) {
  const t = useT();
  const rating = useRating();
  const genreLabel = useGenreLabel();
  const movieTitle = useMovieTitle();
  const { remember } = useWatchlist();

  // A film the group settled on goes into the history by itself. Asking
  // people to press a button to record what they just decided together would
  // be asking them to do the app's bookkeeping.
  useEffect(() => {
    if (finalPick) void remember(finalPick, 'watched');
    // `remember` is rebuilt whenever the list changes, and re-running on that
    // would loop; the pick is what this should react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalPick?.id]);

  const durationMinutes =
    sessionDurationMinutes !== null ? Math.max(0, Math.round(sessionDurationMinutes)) : null;
  const savedMinutes = durationMinutes !== null ? AVERAGE_DECISION_MINUTES - durationMinutes : null;

  const providers = finalPick ? (finalPick.availability[country] ?? []) : [];
  const finalPickTitle = finalPick ? movieTitle(finalPick) : null;
  const links = finalPick ? providerLinks(providers, finalPickTitle ?? finalPick.title) : [];
  const [primaryLink, ...otherLinks] = links;
  const genreLabels = finalPick ? finalPick.genres.map(genreLabel) : [];
  const hasOriginalTitle =
    Boolean(finalPick?.originalTitle) && finalPick?.originalTitle !== finalPickTitle;

  return (
    <div className="screen finish center">
      {finalPick ? (
        <div className="finish__card">
          <div className="finish__panel">
            <div className="finish__eyebrow">{t('finished.title')}</div>
            <h1>{finalPickTitle}</h1>
            {genreLabels.length > 0 ? (
              <div className="finish__panel-genres">
                {genreLabels.map((label) => (
                  <span key={label} className="finish__panel-genre">
                    {label}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="finish__panel-meta">
              {finalPick.year !== null && <span>{finalPick.year}</span>}
              {finalPick.runtimeMinutes !== null && (
                <span>{t('common.minutes', { count: finalPick.runtimeMinutes })}</span>
              )}
              {finalPick.rating != null && <span>★ {rating(finalPick.rating)}</span>}
            </div>
          </div>

          <div className="finish__flow">
            {primaryLink ? (
              <a
                href={primaryLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary btn--block finish__watch-cta"
              >
                ▶ {t('finished.watchNow', { provider: primaryLink.name })}
              </a>
            ) : null}

            {otherLinks.length > 0 ? (
              <p className="finish__also-on small muted">
                {t('finished.alsoOn')}{' '}
                {otherLinks.map((link, index) => (
                  <span key={link.provider}>
                    {index > 0 ? ', ' : ''}
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.name}
                    </a>
                  </span>
                ))}
              </p>
            ) : null}

            <details className="finish__details">
              <summary>{t('finished.details')}</summary>
              <div className="finish__details-body">
                {isCommunityMovie(finalPick.id) ? (
                  <span className="badge badge--community">{t('movie.community')}</span>
                ) : null}
                {hasOriginalTitle && <div className="small muted">{finalPick.originalTitle}</div>}
                <p className="small muted">{finalPick.overview}</p>
                {finalPick.trailer ? (
                  <TrailerPlayer movie={finalPick} trailer={finalPick.trailer} />
                ) : null}
              </div>
            </details>

            {durationMinutes !== null && savedMinutes !== null ? (
              <div className="finish__tip">
                <div className="finish__tip-row">
                  <span className="finish__tip-icon" aria-hidden="true">
                    🦥
                  </span>
                  <div className="finish__tip-copy">
                    <details className="finish__tip-why">
                      <summary>
                        {savedMinutes > 0
                          ? t('finished.tipHeadlineSaved', {
                              minutes: t('common.minutes', { count: savedMinutes }),
                            })
                          : t('finished.tipHeadlineSlow', {
                              duration: t('common.minutes', { count: durationMinutes }),
                            })}
                        <span className="finish__tip-info-icon" aria-hidden="true">
                          i
                        </span>
                      </summary>
                      <p className="small muted">
                        {savedMinutes > 0
                          ? t('finished.donateBodySaved', {
                              duration: t('common.minutes', { count: durationMinutes }),
                              average: t('common.minutes', { count: AVERAGE_DECISION_MINUTES }),
                              minutes: t('common.minutes', { count: savedMinutes }),
                            })
                          : t('finished.donateBodySlow', {
                              duration: t('common.minutes', { count: durationMinutes }),
                              average: t('common.minutes', { count: AVERAGE_DECISION_MINUTES }),
                            })}
                      </p>
                    </details>
                  </div>
                </div>
                <div className="finish__tip-label small muted">{t('finished.tipLabel')}</div>
                <div className="finish__tip-amounts">
                  <a
                    href={paypalAmountUrl(2)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chip"
                  >
                    2 €
                  </a>
                  <a
                    href={paypalAmountUrl(5)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chip chip--selected"
                  >
                    5 €
                  </a>
                  <a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer" className="chip">
                    {t('finished.tipOther')}
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="muted">{t('finished.noPick')}</p>
      )}
      <span className="spacer" />
      <div className="stack">
        {/* Once somebody has started the next round, following them is the
            one thing everybody else here is likely to want. */}
        {nextRoundCode ? (
          <p className="banner small" role="status">
            {t('finished.nextRoundStarted')}
          </p>
        ) : null}
        {/* Blue only when it is the thing to do: with a pick on screen that
            is pressing play, and a second primary button competed with it.
            Joining a round somebody already started, or having no pick at
            all, gives the emphasis back. */}
        <button
          type="button"
          className={nextRoundCode || !finalPick ? 'btn btn--primary btn--block' : 'btn btn--block'}
          onClick={onNextRound}
        >
          {nextRoundCode ? t('finished.joinNextRound') : t('finished.nextRound')}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={onShowResults}>
          {t('finished.viewResults')}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={onNewSession}>
          {t('finished.newSession')}
        </button>
      </div>
    </div>
  );
}
